import type { FetchResult } from '@apollo/client';
import {
  useCreditCheckMutation,
  useCheckCreditCheckStatusLazyQuery,
  type CreditCheckMutation,
  type EncryptedToken,
  type InputMaybe,
  type ServiceabilityTvInternetProductsInput,
  type ServiceabilityPricingInput,
  type ServiceabilityOfferMetadataInput,
  type ServiceabilityOfferMetadata,
  CreditCheckResultEnum,
  type CheckCreditCheckStatusQuery,
} from '@updater/consumer-graph';
import { useReservation } from '@updater/ui-widgets';
import { useFeature } from 'context/growthbook/useFeature';
import { useUserData } from 'flows/core/hooks/use-profile';
import { useCallback, useEffect } from 'react';
import { deepOmitTypename } from '@updater/consumer-graph/utils';

export type CreditCheckStatus = {
  creditCheckResult?: CreditCheckResultEnum;
  ssnPiiToken?: string;
  dobPiiToken?: string;
  hasExecutedCreditCheck: boolean;
};

export interface UseXfinityCreditCheckReturn extends CreditCheckStatus {
  isInXfinityCreditCheckFeature: boolean;
  xfinityDobSsnEnabled: boolean;
  loading: boolean;
  performCreditCheck: ({
    tokenizedDob,
    tokenizedSsn,
  }: {
    tokenizedDob: EncryptedToken;
    tokenizedSsn: EncryptedToken;
  }) => Promise<FetchResult<CreditCheckMutation>>;
  lazyFetchCreditCheckStatus: () => Promise<CreditCheckStatus>;
}

export interface UseXfinityCreditCheckArgs {
  skipCreditCheckQueryOnMount?: boolean;
}

export const useXfinityCreditCheck = (
  { skipCreditCheckQueryOnMount }: UseXfinityCreditCheckArgs = {
    skipCreditCheckQueryOnMount: false,
  }
): UseXfinityCreditCheckReturn => {
  /**
   * Epic - https://updater.atlassian.net/browse/TVI-1868
   * Growthbook - https://growthbook.prod.updatron.tools/features/consumer_app_xfinity_dob_ssn?new
   */
  const { value: xfinityDobSsnEnabled, loading: loadingXfinityDobSsnEnabled } =
    useFeature('consumer_app_xfinity_dob_ssn');

  const { user, loading: userDataLoading } = useUserData();

  const moveId = user?.currentMove?.id ?? '';

  const {
    offer,
    provider,
    loading: reservationLoading,
  } = useReservation({
    moveId,
  });

  const [creditCheckMutation] = useCreditCheckMutation();

  const isXfinityOffer = provider?.code?.toLowerCase() === 'xfinity';
  const isInXfinityCreditCheckFeature = isXfinityOffer && xfinityDobSsnEnabled;

  // Use lazy query instead of a regular query.
  const [
    fetchCreditCheckStatusLazyQuery,
    {
      data: creditCheckStatusData,
      loading: loadingCheckCreditCheckStatus,
      called,
    },
  ] = useCheckCreditCheckStatusLazyQuery();

  // Trigger the lazy query if the conditions are met.
  useEffect(() => {
    if (
      !skipCreditCheckQueryOnMount &&
      isInXfinityCreditCheckFeature &&
      !called &&
      moveId
    ) {
      fetchCreditCheckStatusLazyQuery({
        variables: { moveId },
      });
    }
  }, [
    isInXfinityCreditCheckFeature,
    called,
    moveId,
    fetchCreditCheckStatusLazyQuery,
    skipCreditCheckQueryOnMount,
  ]);

  const performCreditCheck = useCallback(
    ({
      tokenizedDob,
      tokenizedSsn,
    }: {
      tokenizedDob: EncryptedToken;
      tokenizedSsn: EncryptedToken;
    }) => {
      return creditCheckMutation({
        variables: {
          input: {
            offer: {
              code: offer?.code,
              metadata: serviceabilityOfferMetadataInputAdapter({
                metadata:
                  offer?.metadata ?? ({} as ServiceabilityOfferMetadata),
              }),
              name: offer?.name,
              pricing: deepOmitTypename(
                offer?.pricing
              ) as InputMaybe<ServiceabilityPricingInput>,
              products: deepOmitTypename(
                offer?.products
              ) as InputMaybe<ServiceabilityTvInternetProductsInput>,
              providerCode: provider?.code,
              providerOfferId: offer?.providerOfferId,
            },
            tokenizedDob,
            tokenizedSsn,
            moveId,
            customerInformation: {
              firstName: user?.firstName ?? 'not provided',
              lastName: user?.lastName ?? 'not provided',
              email: user?.email ?? 'not provided',
              phone: user?.phone ?? 'not provided',
            },
          },
        },
        refetchQueries: ['CheckCreditCheckStatus'],
      });
    },
    [
      creditCheckMutation,
      moveId,
      offer,
      provider,
      user?.email,
      user?.firstName,
      user?.lastName,
      user?.phone,
    ]
  );

  const lazyFetchCreditCheckStatus = useCallback(async () => {
    if (!moveId) {
      return {
        creditCheckResult: undefined,
        ssnPiiToken: undefined,
        dobPiiToken: undefined,
        hasExecutedCreditCheck: false,
      };
    }

    const { data } = await fetchCreditCheckStatusLazyQuery({
      variables: { moveId },
    });

    const {
      creditCheckResult,
      hasExecutedCreditCheck,
      ssnPiiToken,
      dobPiiToken,
    } = extractCreditCheckData(data);

    return {
      creditCheckResult,
      ssnPiiToken,
      dobPiiToken,
      hasExecutedCreditCheck,
    };
  }, [fetchCreditCheckStatusLazyQuery, moveId]);

  const {
    creditCheckResult,
    checkCreditCheckStatus,
    hasExecutedCreditCheck,
    ssnPiiToken,
    dobPiiToken,
  } = extractCreditCheckData(creditCheckStatusData);

  // When loading === false the result is not immediately ready until the next render
  const isCreditCheckStatusResultReady =
    !isInXfinityCreditCheckFeature ||
    creditCheckResult !== undefined ||
    checkCreditCheckStatus === null;

  const loading =
    loadingXfinityDobSsnEnabled ||
    userDataLoading ||
    reservationLoading ||
    loadingCheckCreditCheckStatus ||
    !isCreditCheckStatusResultReady;

  return {
    xfinityDobSsnEnabled,
    isInXfinityCreditCheckFeature,
    hasExecutedCreditCheck,
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
    loading,
    lazyFetchCreditCheckStatus,
    performCreditCheck,
  };
};

export const serviceabilityOfferMetadataInputAdapter = ({
  metadata,
}: {
  metadata: ServiceabilityOfferMetadata;
}): ServiceabilityOfferMetadataInput => {
  return {
    bulkAgreement: metadata.bulkAgreement,
    cachedAt: metadata.cachedAt,
    dummy: metadata.dummy,
    dummyOfferType: metadata.dummyOfferType,
    fromCache: metadata.fromCache,
    misc: metadata.misc,
  };
};

export const extractCreditCheckData = (data?: CheckCreditCheckStatusQuery) => {
  const checkStatus = data?.checkCreditCheckStatus;
  return {
    creditCheckResult: checkStatus?.creditCheckOutput?.result,
    checkCreditCheckStatus: checkStatus,
    hasExecutedCreditCheck: checkStatus?.creditCheckOutput?.result != null,
    ssnPiiToken: checkStatus?.tokenizedSsn?.token,
    dobPiiToken: checkStatus?.tokenizedDob?.token,
  };
};
